import React, { useState, useEffect } from 'react';
import { tutorApi } from '../services/api';
import '../assets/TutorProfile.css';

const TutorProfile = () => {
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingEducation, setIsEditingEducation] = useState(false);
  const [formData, setFormData] = useState({});
  const [educationList, setEducationList] = useState([]);

  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchTutorProfile();
  }, []);

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const fetchTutorProfile = async () => {
    try {
      setLoading(true);
      const response = await tutorApi.getMyProfile();
      setTutor(response.data);
      setFormData(response.data);
      setEducationList(response.data.educationHistory || []);
      setError('');
    } catch (err) {
      setError('Failed to load profile');
      console.error('Profile fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const parts = name.split('.');
      setFormData(prev => {
        const updated = { ...prev };
        let current = updated;
        for (let i = 0; i < parts.length - 1; i++) {
          current[parts[i]] = { ...current[parts[i]] };
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = value;
        return updated;
      });
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setFormData(tutor);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData(tutor);
  };

  const handleSave = async () => {
    try {
      const response = await tutorApi.updateMyProfile(formData);
      setTutor(response.data);
      setFormData(response.data);
      setIsEditing(false);
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update profile: ' + (err.response?.data?.message || err.message));
      setTimeout(() => setError(''), 3000);
    }
  };

  // Education management
  const handleEditEducation = () => {
    setIsEditingEducation(true);
    setEducationList(tutor.educationHistory || []);
  };

  const handleCancelEducation = () => {
    setIsEditingEducation(false);
    setEducationList(tutor.educationHistory || []);
  };

  const handleSaveEducation = async () => {
    try {
      const updateData = {
        ...tutor,
        educationHistory: educationList
      };
      const response = await tutorApi.updateMyProfile(updateData);
      setTutor(response.data);
      setEducationList(response.data.educationHistory || []);
      setIsEditingEducation(false);
      setSuccess('Education history updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update education history: ' + (err.response?.data?.message || err.message));
      setTimeout(() => setError(''), 3000);
    }
  };

  const addEducation = () => {
    setEducationList([...educationList, {
      level: '',
      institution: '',
      major: '',
      yearGraduated: '',
      subjects: '',
      grades: ''
    }]);
  };

  const removeEducation = (index) => {
    setEducationList(educationList.filter((_, i) => i !== index));
  };

  const updateEducation = (index, field, value) => {
    const updated = [...educationList];
    updated[index] = { ...updated[index], [field]: value };
    setEducationList(updated);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      setUploadingImage(true);
      const response = await tutorApi.uploadProfileImage(formData);
      setTutor(prev => ({ ...prev, profileImage: response.data.profileImage }));
      setSuccess('Profile image updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to upload image: ' + (err.response?.data?.message || err.message));
      setTimeout(() => setError(''), 3000);
    } finally {
      setUploadingImage(false);
    }
  };

  if (loading) return <div className="loading">Loading profile...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!tutor) return <div className="error">Profile not found</div>;

  return (
    <div className="tutor-profile">
      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="profile-header">
        <div className="profile-header-main">
          <div className="profile-avatar profile-avatar-upload" onClick={() => document.getElementById('avatar-upload').click()}>
            {tutor.profileImage ? (
              <img src={tutor.profileImage} alt="Profile" className="profile-avatar-img" />
            ) : (
              tutor.name.charAt(0).toUpperCase()
            )}
            <div className="avatar-upload-overlay">
              {uploadingImage ? '...' : (
                <>
                  {tutor.profileImage && (
                    <button className="avatar-overlay-btn" title="View photo" onClick={(e) => { e.stopPropagation(); window.open(tutor.profileImage, '_blank'); }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="3" stroke="#fff" strokeWidth="1.5"/>
                        <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                  <button className="avatar-overlay-btn" title="Upload photo" onClick={(e) => { e.stopPropagation(); document.getElementById('avatar-upload').click(); }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 2v8M4 6l4-4 4 4M2 12h12" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  {tutor.profileImage && (
                    <button className="avatar-overlay-btn remove" title="Remove photo" onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await tutorApi.removeProfileImage();
                        setTutor(prev => ({ ...prev, profileImage: null }));
                        setSuccess('Profile image removed!');
                        setTimeout(() => setSuccess(''), 3000);
                      } catch (err) {
                        setError('Failed to remove image');
                        setTimeout(() => setError(''), 3000);
                      }
                    }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                      </svg>
                    </button>
                  )}
                </>
              )}
            </div>
            <input id="avatar-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
          </div>
          <div className="profile-header-info">
            <h1>{tutor.name}</h1>
            <p className="profile-email">{tutor.email}</p>
            <div className="profile-badges">
              <span className={`status-badge-new ${tutor.isActive ? 'active' : 'inactive'}`}>
                <span className="badge-dot"></span>
                {tutor.isActive ? 'Active' : 'Inactive'}
              </span>
              <span className="role-badge-new">Tutor</span>
              {tutor.code && (
                <span className="code-badge-new">{tutor.code}</span>
              )}
            </div>
            {(tutor.createdAt || tutor.updatedAt) && (
              <div className="profile-timestamps-footer">
                {tutor.createdAt && (
                  <span>Created {new Date(tutor.createdAt).toLocaleString('en-SG', { 
                    timeZone: 'Asia/Singapore',
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                )}
                {tutor.createdAt && tutor.updatedAt && <span className="timestamp-separator">•</span>}
                {tutor.updatedAt && (
                  <span>Last modified {new Date(tutor.updatedAt).toLocaleString('en-SG', { 
                    timeZone: 'Asia/Singapore',
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="profile-content">

        <div className={`profile-section editable-section ${isEditing ? 'editing' : ''}`}>
          <button 
            className="btn-edit-corner" 
            onClick={() => isEditing ? handleCancel() : handleEdit()}
            title={isEditing ? "Cancel" : "Edit"}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              {isEditing ? (
                <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              ) : (
                <path d="M10 1.5a1.5 1.5 0 0 1 2.1 2.1L4.5 11.1l-3 .4.4-3L10 1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              )}
            </svg>
          </button>
          
          <h2>Personal Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Email</label>
              <p>{tutor.email}</p>
            </div>
            <div className="info-item">
              <label>Date of Birth (Age)</label>
              {isEditing ? (
                <input 
                  type="date" 
                  name="dateOfBirth" 
                  value={formData.dateOfBirth ? new Date(formData.dateOfBirth).toLocaleDateString('en-CA', { timeZone: 'Asia/Singapore' }) : ''} 
                  onChange={handleInputChange}
                />
              ) : (
                <p>
                  {tutor.dateOfBirth ? (
                  `${new Date(tutor.dateOfBirth).toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore' })} (${calculateAge(tutor.dateOfBirth)} years)`
                  ) : tutor.age ? (
                    `Age: ${tutor.age}`
                  ) : (
                    'N/A'
                  )}
                </p>
              )}
            </div>
            <div className="info-item">
              <label>Gender</label>
              {isEditing ? (
                <select name="gender" value={formData.gender || ''} onChange={handleInputChange}>
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              ) : (
                <p>{tutor.gender || 'N/A'}</p>
              )}
            </div>
            <div className="info-item">
              <label>Race</label>
              {isEditing ? (
                <input 
                  type="text" 
                  name="race" 
                  value={formData.race || ''} 
                  onChange={handleInputChange}
                  placeholder="e.g., Chinese, Malay, Indian"
                />
              ) : (
                <p>{tutor.race || 'N/A'}</p>
              )}
            </div>
            <div className="info-item">
              <label>Phone</label>
              {isEditing ? (
                <input 
                  type="tel" 
                  name="contact.phone" 
                  value={formData.contact?.phone || ''} 
                  onChange={handleInputChange}
                  placeholder="+65 9123 4567"
                />
              ) : (
                <p>{tutor.contact?.phone || 'N/A'}</p>
              )}
            </div>
            <div className="info-item">
              <label>Country</label>
              {isEditing ? (
                <input 
                  type="text" 
                  name="country" 
                  value={formData.country || 'Singapore'} 
                  onChange={handleInputChange}
                  placeholder="Singapore"
                />
              ) : (
                <p>{tutor.country || 'Singapore'}</p>
              )}
            </div>
            <div className="info-item">
              <label>Address</label>
              {isEditing ? (
                <input 
                  type="text" 
                  name="contact.address" 
                  value={formData.contact?.address || ''} 
                  onChange={handleInputChange}
                  placeholder="Full address"
                />
              ) : (
                <p>{tutor.contact?.address || 'N/A'}</p>
              )}
            </div>
            <div className="info-item">
              <label>LinkedIn</label>
              {isEditing ? (
                <input 
                  type="url" 
                  name="linkedin" 
                  value={formData.linkedin || ''} 
                  onChange={handleInputChange}
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              ) : (
                <p>
                  {tutor.linkedin ? (
                    <a href={tutor.linkedin} target="_blank" rel="noopener noreferrer" className="linkedin-link">
                      {tutor.linkedin}
                    </a>
                  ) : 'N/A'}
                </p>
              )}
            </div>
            <div className="info-item full-width">
              <label>Bio</label>
              {isEditing ? (
                <textarea 
                  name="bio" 
                  value={formData.bio || ''} 
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Tell us about yourself, your teaching experience, and qualifications..."
                />
              ) : (
                <p className="bio-text">{tutor.bio || 'N/A'}</p>
              )}
            </div>
          </div>
          
          {isEditing && (
            <div className="section-actions">
              <button className="btn-save-section" onClick={handleSave}>Save</button>
            </div>
          )}
        </div>

        <div className={`profile-section editable-section ${isEditingEducation ? 'editing' : ''}`}>
          <button 
            className="btn-edit-corner" 
            onClick={() => isEditingEducation ? handleCancelEducation() : handleEditEducation()}
            title={isEditingEducation ? "Cancel" : "Edit"}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              {isEditingEducation ? (
                <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              ) : (
                <path d="M10 1.5a1.5 1.5 0 0 1 2.1 2.1L4.5 11.1l-3 .4.4-3L10 1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              )}
            </svg>
          </button>
          
          <h2>Education History</h2>
          
          {isEditingEducation ? (
            <>
              <div className="education-section">
                {educationList.length === 0 ? (
                  <p className="no-data">No education added. Click "Add Education" to start.</p>
                ) : (
                  educationList.map((edu, index) => (
                    <div key={index} className="education-entry editable">
                      <button className="btn-remove-education" onClick={() => removeEducation(index)} title="Remove">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                      <div className="education-entry-header">
                        <h3>Education {index + 1}</h3>
                      </div>
                      <div className="education-form">
                        <div className="form-row">
                          <div className="form-group">
                            <label>Level</label>
                            <select value={edu.level || ''} onChange={(e) => updateEducation(index, 'level', e.target.value)}>
                              <option value="">Select level</option>
                              <option value="GCE 'O' Level">GCE 'O' Level</option>
                              <option value="GCE 'A' Level">GCE 'A' Level</option>
                              <option value="IB Diploma">IB Diploma</option>
                              <option value="Diploma">Diploma</option>
                              <option value="Bachelor's Degree">Bachelor's Degree</option>
                              <option value="Master's Degree">Master's Degree</option>
                              <option value="PhD">PhD</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Institution</label>
                            <input type="text" value={edu.institution || ''} onChange={(e) => updateEducation(index, 'institution', e.target.value)} placeholder="School/University name" />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Major/Field</label>
                            <input type="text" value={edu.major || ''} onChange={(e) => updateEducation(index, 'major', e.target.value)} placeholder="e.g., Mathematics" />
                          </div>
                          <div className="form-group">
                            <label>Year Graduated</label>
                            <input type="number" value={edu.yearGraduated || ''} onChange={(e) => updateEducation(index, 'yearGraduated', e.target.value)} placeholder="e.g., 2020" />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Subjects</label>
                            <input type="text" value={edu.subjects || ''} onChange={(e) => updateEducation(index, 'subjects', e.target.value)} placeholder="e.g., Math, Physics, Chemistry" />
                          </div>
                          <div className="form-group">
                            <label>Grades</label>
                            <input type="text" value={edu.grades || ''} onChange={(e) => updateEducation(index, 'grades', e.target.value)} placeholder="e.g., A1, A2, B3" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <button className="btn-add-education" onClick={addEducation}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Add Education
              </button>
              <div className="section-actions">
                <button className="btn-save-section" onClick={handleSaveEducation}>Save</button>
              </div>
            </>
          ) : (
            <div className="education-list">
              {(!tutor.educationHistory || tutor.educationHistory.length === 0) ? (
                <p className="no-data">No education history added yet.</p>
              ) : (
                [...tutor.educationHistory]
                  .sort((a, b) => (b.yearGraduated || 0) - (a.yearGraduated || 0))
                  .map((edu, index) => (
                  <div key={index} className="education-card">
                    <div className="education-header">
                      <h3>{edu.level}</h3>
                      {edu.yearGraduated && <span className="year">{edu.yearGraduated}</span>}
                    </div>
                    {edu.institution && <p className="institution">{edu.institution}</p>}
                    {edu.major && <p className="major">{edu.major}</p>}
                    {edu.subjects && <p className="subjects">Subjects: {edu.subjects}</p>}
                    {edu.grades && <p className="grades">Grades: {edu.grades}</p>}
                  </div>
                ))
              )}
            </div>
          )}
        </div>


      </div>
    </div>
  );
};

export default TutorProfile;
